import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type CurrencyCodes,
  Metadata,
  Positions,
  Renderer,
  type ReportFilter,
  TimeInterval,
  TimeSettingsMode,
} from "@doitintl/cmp-models";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { analyticsAlertText, globalText } from "../../../../assets/texts";
import { budgetTxt } from "../../../../assets/texts/CloudAnalytics/budget";
import { useAnalyticsDimensions } from "../../../../Components/hooks/cloudAnalytics/useAnalyticsDimensions/useAnalyticsDimensions";
import { useCloudAnalyticsMetadata } from "../../../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { InfoTooltip } from "../../../../Components/InfoTooltip/InfoTooltip";
import { useAttributionsContext } from "../../../../Context/AttributionsContext";
import { type BudgetInfo, type MetadataOption } from "../../../../types";
import {
  formatDecimalNumber,
  getCurrencySymbol,
  onKeyPressPreventNonNumeric,
  onKeyPressPreventNonNumericAllowFloat,
} from "../../../../utils/common";
import { colsMap } from "../../generateReport/utils";
import PreviewReport, { type PreviewData } from "../../previewReport/PreviewReport";
import { getDefaultForecastSettings } from "../../report/config/ForecastSettings/utils";
import { QueryType } from "../../report/ReportQuery";
import { convertStringToDataRecord, extractCloudProviders } from "../../report/ReportQueryUtils";
import { type CurrentLastPeriods } from "../../ReportData";
import {
  budgetConfigurationDynamicOptions,
  BudgetConfigurationOptions,
  BudgetConfigurations,
  BudgetDynamicConfigurations,
  BudgetTypes,
  CurrencyOptions,
  getCurrencyOption,
  getCurrentPeriodIndex,
  getSeasonalAmountsLength,
  timeIntervalOptions,
} from "../../utilities";
import { getAlertAmountFromPercentage } from "../shared";
import { getFixedTimeInterval, getRolloverLimit, getTimeRangeOption } from "../utils";
import BudgetInfoPanel from "./BudgetInfoPanel";
import PeriodPullOver from "./PeriodPullOver";

type Props = {
  setOverrideStep: (step: number) => void;
  dimensionOptions: MetadataOption[];
  budgetInfo: BudgetInfo;
  setBudgetInfo: Dispatch<SetStateAction<BudgetInfo>>;
  isCurrentUserEditor: boolean;
  currentLastPeriods: CurrentLastPeriods;
  setCurrentLastPeriods: Dispatch<SetStateAction<CurrentLastPeriods>>;
  setThresholdsAmount: Dispatch<SetStateAction<number>>;
  initialSeasonalAmounts?: number[];
  onSeasonalAmountsValidityChange: (isValid: boolean) => void;
};

const AMORTIZED_COST_METRIC = 5;
const AMORTIZED_COST_EXTENDED_METRIC = "amortized_cost";

const Step2 = ({
  setOverrideStep,
  budgetInfo,
  setBudgetInfo,
  isCurrentUserEditor,
  currentLastPeriods,
  setCurrentLastPeriods,
  setThresholdsAmount,
  initialSeasonalAmounts,
  onSeasonalAmountsValidityChange,
}: Props) => {
  const { attributions } = useAttributionsContext();
  const { metadata: metadataSnapshots } = useCloudAnalyticsMetadata();
  const [renderer, setRenderer] = useState(Renderer.STACKED_COLUMN_CHART);
  const seasonalAmountsMapRef = useRef(new Map<TimeInterval, number[]>());

  const [lastPeriodCostLoading, setLastPeriodCostLoading] = useState<boolean>(false);
  const [refreshPreview, setRefreshPreview] = useState<boolean>(false);
  const { dimensions } = useAnalyticsDimensions({
    metadataSnapshots,
    attributions,
  });

  useEffect(() => {
    if (budgetInfo.seasonalAmounts?.length && budgetInfo.currentTypeAndFrequency.period) {
      seasonalAmountsMapRef.current.set(budgetInfo.currentTypeAndFrequency.period, [...budgetInfo.seasonalAmounts]);
    }
  }, [budgetInfo.seasonalAmounts, budgetInfo.currentTypeAndFrequency.period]);

  const handleAmortizedCostChange = useCallback(
    (event) => {
      setBudgetInfo((prev) => ({
        ...prev,
        amortizedCost: event.target.checked,
      }));
    },
    [setBudgetInfo]
  );

  const handleChangeAmount = useCallback(
    (event) => {
      const value = event.target.value;
      if (value === "") {
        setBudgetInfo((prev) => ({ ...prev, budgetAmount: 0 }));
        return;
      }
      const newAmount = parseFloat(value);
      setBudgetInfo((prev) => ({ ...prev, budgetAmount: isNaN(newAmount) ? 0 : newAmount }));
    },
    [setBudgetInfo]
  );

  const handleBudgetConfigurationOptionChanged = useCallback(
    (event) => {
      setBudgetInfo((prev) => {
        const temp = { ...prev };
        const type = event.target.value;
        temp.configurationOption = {
          type,
          dynamic: type === BudgetConfigurations.DYNAMIC ? BudgetDynamicConfigurations.LAST_PERIOD : undefined,
        };

        if (type === BudgetConfigurations.SPECIFY) {
          const expectedLength = getSeasonalAmountsLength(prev.currentTypeAndFrequency.period);
          const savedValues = seasonalAmountsMapRef.current.get(prev.currentTypeAndFrequency.period);
          if (savedValues?.length === expectedLength) {
            temp.seasonalAmounts = [...savedValues];
          } else if (initialSeasonalAmounts?.length === expectedLength) {
            temp.seasonalAmounts = [...initialSeasonalAmounts];
          } else {
            temp.seasonalAmounts = Array(expectedLength).fill(0);
          }
        } else {
          temp.seasonalAmounts = prev.seasonalAmounts;
        }

        return temp;
      });
    },
    [setBudgetInfo, initialSeasonalAmounts]
  );

  const previewData = useMemo(() => {
    if (!dimensions) {
      return;
    }
    const filters =
      budgetInfo.filters?.flatMap((f) => {
        const md = dimensions?.find((d) => f.id === d.id);

        const filter: ReportFilter = {
          ...f,
          type: (f.type || md?.data.type) ?? "",
          position: md?._position,
          id: f.id,
          field: (f.field || md?.data.field) ?? "",
          key: (f.key || md?.data.key) ?? "",
          includeInFilter: true,
          limit: 0,
          limitMetric: 0,
          composite: [],
          regexp: md?._regexp ?? null,
          values: f.values ?? [],
        };
        return filter;
      }) ?? [];

    const cloudProviders = extractCloudProviders(dimensions);

    const timeIntervalOption = timeIntervalOptions.find((t) => t.value === budgetInfo.currentTypeAndFrequency.period);

    dimensions?.forEach((d) => {
      if (filters.some((f) => f.id === d.id)) {
        d._position = Positions.ROW;
      }

      if (d._position === Positions.COL) {
        if (d.data.key === timeIntervalOption?.value || d.data.key === "year") {
          d._visible = true;
        }
      }
    });

    const rows = convertStringToDataRecord(
      dimensions,
      filters.map((f) => f.id)
    );
    const cols = convertStringToDataRecord(dimensions, colsMap[budgetInfo.currentTypeAndFrequency.period]);
    const attribution = filters.find((f) => f.id === `${Metadata.ATTRIBUTION}:${Metadata.ATTRIBUTION}`);
    const attributionIds = attribution ? attribution.values : [];
    const initialScope = budgetInfo.scope.map((f) => f.ref.id);
    const timeRange = {
      mode:
        budgetInfo.currentTypeAndFrequency.type === BudgetTypes.FIXED ? TimeSettingsMode.Fixed : TimeSettingsMode.Last,
      time: budgetInfo.currentTypeAndFrequency.period,
      range:
        budgetInfo.currentTypeAndFrequency.type === BudgetTypes.FIXED
          ? { start: budgetInfo.startPeriod, end: budgetInfo.endPeriod }
          : undefined,
      amount: 5,
    };

    const previewData: PreviewData = {
      currency: budgetInfo.currency,
      scope: initialScope.length > 0 ? initialScope : (attributionIds ?? []),
      attributionGroups: [],
      timeInterval:
        budgetInfo.currentTypeAndFrequency.type === BudgetTypes.RECURRING
          ? budgetInfo.currentTypeAndFrequency.period
          : getFixedTimeInterval(budgetInfo.startPeriod, budgetInfo.endPeriod),
      timeRangeOptions: getTimeRangeOption(
        budgetInfo.currentTypeAndFrequency.type,
        budgetInfo.currentTypeAndFrequency.period,
        budgetInfo.startPeriod,
        budgetInfo.endPeriod
      ),
      attributionGroupsPayload: [],
      rows,
      filters,
      cols,
      cloudProviders,
      dataSource: budgetInfo.dataSource,
      forecastSettings: getDefaultForecastSettings(timeRange, budgetInfo.currentTypeAndFrequency.period, "grouping"),
      type: QueryType.BUDGET,
    };
    if (budgetInfo.amortizedCost) {
      previewData.extendedMetric = AMORTIZED_COST_EXTENDED_METRIC;
      previewData.metric = AMORTIZED_COST_METRIC;
    }
    return previewData;
  }, [
    dimensions,
    budgetInfo.filters,
    budgetInfo.currentTypeAndFrequency.period,
    budgetInfo.currentTypeAndFrequency.type,
    budgetInfo.scope,
    budgetInfo.currency,
    budgetInfo.startPeriod,
    budgetInfo.endPeriod,
    budgetInfo.dataSource,
    budgetInfo.amortizedCost,
  ]);
  const isInit = useRef(true);

  useEffect(() => {
    if (previewData) {
      if (!isInit.current) {
        setRefreshPreview(true);
      } else {
        isInit.current = false;
      }
    }
  }, [previewData]);

  const handleSeasonalAmountsChange = useCallback(
    (amounts: number[]) => {
      seasonalAmountsMapRef.current.set(budgetInfo.currentTypeAndFrequency.period, amounts);
      setBudgetInfo((prev) => ({
        ...prev,
        seasonalAmounts: amounts,
      }));
    },
    [setBudgetInfo, budgetInfo.currentTypeAndFrequency.period]
  );

  const handleValidityChange = useCallback(
    (isValid: boolean) => {
      onSeasonalAmountsValidityChange(isValid);
    },
    [onSeasonalAmountsValidityChange]
  );

  useEffect(() => {
    let newAmount = budgetInfo.budgetAmount;
    let shouldUpdate = false;

    // Handle seasonal budgets - use amount for current period
    if (budgetInfo.configurationOption.type === BudgetConfigurations.SPECIFY && budgetInfo.seasonalAmounts) {
      // Get the index of current period
      const currentPeriodIndex = getCurrentPeriodIndex(
        budgetInfo.currentTypeAndFrequency.period,
        budgetInfo.startPeriod.year
      );
      // Get the seasonal amount for current period
      const seasonalAmount = budgetInfo.seasonalAmounts[currentPeriodIndex];
      if (seasonalAmount !== budgetInfo.budgetAmount) {
        newAmount = seasonalAmount;
        shouldUpdate = true;
      }
    } else if (currentLastPeriods?.lastPeriod) {
      // Handle budgets based on last period's actual cost
      // Round to 2 decimal places for currency
      const lastPeriodBase = Math.round((currentLastPeriods.lastPeriod ?? 0) * 100) / 100;

      // Simple last period - just use the actual cost
      if (budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD) {
        if (lastPeriodBase !== budgetInfo.budgetAmount) {
          newAmount = lastPeriodBase;
          shouldUpdate = true;
        }
      } else if (
        budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH
      ) {
        // Last period + growth percentage
        // Calculate new amount by applying growth percentage to last period
        const growthAmount = lastPeriodBase * (budgetInfo.growthPerPeriod / 100 + 1);
        if (growthAmount !== budgetInfo.budgetAmount) {
          newAmount = growthAmount;
          shouldUpdate = true;
        }
      }
    } else if (budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.PERCENTAGE_GROWTH) {
      // Handle pure percentage growth (without last period)
      // Work backwards to find base amount before growth
      const baseAmount = budgetInfo.budgetAmount / (budgetInfo.growthPerPeriod / 100 + 1);
      // Apply growth to base amount
      const growthAmount = baseAmount * (budgetInfo.growthPerPeriod / 100 + 1);
      // Use small epsilon for floating point comparison
      if (Math.abs(growthAmount - budgetInfo.budgetAmount) > 0.01) {
        newAmount = growthAmount;
        shouldUpdate = true;
      }
    }

    // Only update state if amount has changed
    if (shouldUpdate) {
      setBudgetInfo((prev) => ({
        ...prev,
        budgetAmount: newAmount,
      }));
    }
  }, [
    budgetInfo.configurationOption.type,
    budgetInfo.configurationOption.dynamic,
    budgetInfo.growthPerPeriod,
    budgetInfo.seasonalAmounts,
    budgetInfo.startPeriod.year,
    budgetInfo.currentTypeAndFrequency.period,
    currentLastPeriods?.lastPeriod,
    budgetInfo.budgetAmount,
    setBudgetInfo,
  ]);

  useEffect(() => {
    setThresholdsAmount(budgetInfo.budgetAmount);
    setBudgetInfo((prev) => ({
      ...prev,
      alerts: prev.alerts.map((a) => ({
        ...a,
        amount: getAlertAmountFromPercentage(a.percentage, budgetInfo.budgetAmount),
      })),
    }));
  }, [budgetInfo.budgetAmount, setThresholdsAmount, setBudgetInfo]);

  const [growthInputEmpty, setGrowthInputEmpty] = useState(false);

  const onGrowthInputChanged = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      setGrowthInputEmpty(true);
      return;
    }
    setGrowthInputEmpty(false);
    if (inputValue === "0" || inputValue.endsWith(".") || /^0\.\d*$/.test(inputValue) || /^\d+\.$/.test(inputValue)) {
      setBudgetInfo((prev) => ({
        ...prev,
        growthPerPeriod: inputValue,
      }));
      return;
    }
    const val = parseFloat(inputValue);
    if (!isNaN(val) && val >= 0 && val <= 100) {
      setBudgetInfo((prev) => ({
        ...prev,
        growthPerPeriod: val,
      }));
    }
  };

  const getCurrentLastPeriods = useCallback(
    (periods) => {
      if (!periods) {
        return;
      }
      if (
        periods.lastPeriod !== currentLastPeriods?.lastPeriod ||
        periods.currentPeriod !== currentLastPeriods?.currentPeriod
      ) {
        setCurrentLastPeriods(periods);
      }
    },
    [currentLastPeriods?.currentPeriod, currentLastPeriods?.lastPeriod, setCurrentLastPeriods]
  );

  const onBudgetConfigurationDynamicChanged = useCallback(
    (e) => {
      const value = e.target.value as BudgetDynamicConfigurations;
      setBudgetInfo((prev) => {
        const isLastPeriodBased =
          value === BudgetDynamicConfigurations.LAST_PERIOD ||
          value === BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH;

        return {
          ...prev,
          configurationOption: {
            ...prev.configurationOption,
            dynamic: value,
          },
          rolloverLimit: value === BudgetDynamicConfigurations.NEXT_PERIOD_ROLLOVER ? 1 : 0,
          budgetAmount: isLastPeriodBased ? 0 : prev.budgetAmount,
        };
      });
    },
    [setBudgetInfo]
  );

  useEffect(() => {
    const maxRolloverLimit = getRolloverLimit(budgetInfo.currentTypeAndFrequency.period);
    if ((budgetInfo.rolloverLimit ?? 0) > maxRolloverLimit) {
      setBudgetInfo((prev) => ({
        ...prev,
        rolloverLimit: maxRolloverLimit,
      }));
    }
  }, [budgetInfo.currentTypeAndFrequency.period, budgetInfo.rolloverLimit, setBudgetInfo]);

  const getBudgetAmountDisplayValue = useCallback(() => {
    const isLastPeriodBased =
      budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD ||
      budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH;

    if (isLastPeriodBased) {
      return currentLastPeriods?.lastPeriod ? Math.round(currentLastPeriods.lastPeriod * 100) / 100 : "";
    }

    return budgetInfo.budgetAmount === 0 ? "" : budgetInfo.budgetAmount;
  }, [budgetInfo.configurationOption.dynamic, budgetInfo.budgetAmount, currentLastPeriods?.lastPeriod]);

  const formattedLastPeriodCost = useMemo(() => {
    if (currentLastPeriods?.lastPeriod) {
      const roundedValue = Math.round(currentLastPeriods.lastPeriod * 100) / 100;
      return `${getCurrencySymbol(budgetInfo.currency)}${formatDecimalNumber(
        roundedValue,
        roundedValue % 1 === 0 ? 0 : 2
      )}`;
    }

    return globalText.NA;
  }, [currentLastPeriods?.lastPeriod, budgetInfo.currency]);

  return (
    <Stack
      sx={{
        gap: 3,
        mt: 4,
      }}
    >
      <BudgetInfoPanel
        typeAndFrequency={budgetInfo.currentTypeAndFrequency.label}
        startDate={budgetInfo.startPeriod.toFormat("dd LLL, yyyy")}
        endDate={
          budgetInfo.currentTypeAndFrequency.type === BudgetTypes.FIXED
            ? budgetInfo.endPeriod?.toFormat("dd LLL, yyyy")
            : undefined
        }
        setOverrideStep={setOverrideStep}
        filters={budgetInfo.filters}
        dimensionOptions={dimensions ?? []}
        scope={budgetInfo.scope}
      />
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            mb: 2,
          }}
        >
          {budgetTxt.CREATE_BUDGET.BUDGET_AMOUNT}
        </Typography>

        <Autocomplete
          sx={{
            mb: 3,
          }}
          disabled={!isCurrentUserEditor}
          onChange={(_, val) => {
            if (val) {
              setBudgetInfo((prev) => ({ ...prev, currency: val.split(" ")[0] as CurrencyCodes }));
            }
          }}
          options={CurrencyOptions.map((c) => getCurrencyOption(c))}
          value={getCurrencyOption(budgetInfo.currency)}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={analyticsAlertText.STEPPER.CURRENCY}
              placeholder={analyticsAlertText.STEPPER.SELECT_CURRENCY}
              fullWidth
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
        />

        <TextField
          required
          variant="outlined"
          type="number"
          label={budgetTxt.CREATE_BUDGET.BUDGET_AMOUNT}
          fullWidth
          size="small"
          value={getBudgetAmountDisplayValue()}
          onChange={handleChangeAmount}
          onKeyUp={onKeyPressPreventNonNumeric}
          disabled={
            !isCurrentUserEditor ||
            budgetInfo.configurationOption.type === BudgetConfigurations.SPECIFY ||
            budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD ||
            budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">{getCurrencySymbol(budgetInfo.currency)}</InputAdornment>
              ),
            },

            htmlInput: { min: 0 },

            inputLabel: {
              shrink: true,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            mt: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {budgetTxt.CREATE_BUDGET.LAST_PERIOD_ACTUAL_COST}:
          {lastPeriodCostLoading ? (
            <CircularProgress size={12} thickness={5} color="inherit" />
          ) : (
            <b>{formattedLastPeriodCost}</b>
          )}
        </Typography>
      </Box>
      {budgetInfo.currentTypeAndFrequency.type !== BudgetTypes.FIXED && (
        <>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                mb: 1,
              }}
            >
              {budgetTxt.CREATE_BUDGET.BUDGET_CONFIGURATION}
            </Typography>

            <RadioGroup
              sx={{
                pl: 1,
              }}
              value={budgetInfo.configurationOption.type}
              onChange={handleBudgetConfigurationOptionChanged}
            >
              {BudgetConfigurationOptions.map((option) => (
                <Box key={option.value}>
                  <Tooltip
                    title={
                      option.value === BudgetConfigurations.SPECIFY &&
                      budgetInfo.currentTypeAndFrequency.period === TimeInterval.WEEK
                        ? budgetTxt.CREATE_BUDGET.WEEKLY_BUDGET_SEASONALITY_TOOLTIP
                        : ""
                    }
                    placement="bottom-start"
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                      disabled={
                        !isCurrentUserEditor ||
                        (option.value === BudgetConfigurations.SPECIFY &&
                          budgetInfo.currentTypeAndFrequency.period === TimeInterval.WEEK)
                      }
                    />
                  </Tooltip>
                  {option.value === BudgetConfigurations.DYNAMIC &&
                    budgetInfo.configurationOption.type === BudgetConfigurations.DYNAMIC && (
                      <Box
                        sx={{
                          maxWidth: "552px",
                        }}
                      >
                        <Select
                          fullWidth
                          value={budgetInfo.configurationOption.dynamic}
                          onChange={onBudgetConfigurationDynamicChanged}
                          size="small"
                        >
                          {budgetConfigurationDynamicOptions.map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              sx={{
                                maxWidth: 552,
                                whiteSpace: "break-spaces",
                              }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>

                        {(budgetInfo.configurationOption.dynamic === BudgetDynamicConfigurations.PERCENTAGE_GROWTH ||
                          budgetInfo.configurationOption.dynamic ===
                            BudgetDynamicConfigurations.LAST_PERIOD_AND_PERCENTAGE_GROWTH) && (
                          <TextField
                            value={
                              growthInputEmpty || budgetInfo.growthPerPeriod === 0 ? "" : budgetInfo.growthPerPeriod
                            }
                            onChange={onGrowthInputChanged}
                            onKeyDown={onKeyPressPreventNonNumericAllowFloat}
                            onBlur={(e) => {
                              if (growthInputEmpty) {
                                setGrowthInputEmpty(false);
                                setBudgetInfo((prev) => ({ ...prev, growthPerPeriod: 0 }));
                                return;
                              }
                              const val = parseFloat(e.target.value);
                              if (isNaN(val) || val < 0) {
                                setBudgetInfo((prev) => ({ ...prev, growthPerPeriod: 0 }));
                              } else if (val > 100) {
                                setBudgetInfo((prev) => ({ ...prev, growthPerPeriod: 100 }));
                              } else {
                                setBudgetInfo((prev) => ({ ...prev, growthPerPeriod: val }));
                              }
                            }}
                            sx={{ mt: 2 }}
                            fullWidth
                            required
                            label={budgetTxt.CREATE_BUDGET.GROWTH}
                            disabled={!isCurrentUserEditor}
                            type="text"
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">%</InputAdornment>,
                                inputMode: "decimal",
                              },
                            }}
                          />
                        )}

                        {budgetInfo.configurationOption.dynamic ===
                          BudgetDynamicConfigurations.NEXT_PERIOD_ROLLOVER && (
                          <TextField
                            required
                            sx={{
                              mt: 2,
                            }}
                            variant="outlined"
                            type="number"
                            fullWidth
                            size="small"
                            slotProps={{
                              input: {
                                inputProps: {
                                  min: 1,
                                  max: getRolloverLimit(budgetInfo.currentTypeAndFrequency.period),
                                },
                              },
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            label={budgetTxt.CREATE_BUDGET.ROLLOVER_MONTHS_TITLE}
                            value={budgetInfo.rolloverLimit ?? ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              setBudgetInfo((prev) => {
                                if (inputValue === "") {
                                  return { ...prev, rolloverLimit: undefined };
                                }
                                const val = parseInt(inputValue, 10);
                                const max = getRolloverLimit(prev.currentTypeAndFrequency.period);
                                return { ...prev, rolloverLimit: isNaN(val) ? undefined : Math.min(val, max) };
                              });
                            }}
                            onKeyUp={onKeyPressPreventNonNumeric}
                            disabled={!isCurrentUserEditor}
                          />
                        )}
                      </Box>
                    )}
                </Box>
              ))}
            </RadioGroup>
          </Box>

          {budgetInfo.configurationOption.type === BudgetConfigurations.SPECIFY && (
            <PeriodPullOver
              currency={budgetInfo.currency}
              disabled={!isCurrentUserEditor}
              seasonalAmounts={budgetInfo.seasonalAmounts ?? []}
              onAmountsChange={handleSeasonalAmountsChange}
              periodType={budgetInfo.currentTypeAndFrequency.period}
              startDate={budgetInfo.startPeriod}
              onValidityChange={handleValidityChange}
            />
          )}
        </>
      )}
      <Box>
        <Typography variant="subtitle1" fontWeight={500} mb={1}>
          {budgetTxt.CREATE_BUDGET.ADVANCED_SETTINGS}
        </Typography>
        <FormGroup
          sx={{
            pl: 1,
          }}
        >
          <InfoTooltip description={budgetTxt.CREATE_BUDGET.AMORTIZED_COST_TOOLTIP} placement="right">
            <FormControlLabel
              sx={{
                marginRight: 0,
              }}
              control={<Checkbox checked={budgetInfo.amortizedCost} onChange={handleAmortizedCostChange} />}
              label={budgetTxt.CREATE_BUDGET.USE_AMORTIZED_COST}
            />
          </InfoTooltip>
        </FormGroup>
      </Box>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            mb: 1,
          }}
        >
          {budgetTxt.CREATE_BUDGET.BUDGET_PREVIEW}
        </Typography>
      </Box>
      {previewData && (
        <PreviewReport
          attributions={attributions}
          displayForecast
          previewData={previewData}
          runQuery={refreshPreview}
          renderer={renderer}
          setRenderer={setRenderer}
          setRunQuery={(reset) => {
            setRefreshPreview(reset ?? false);
          }}
          getCurrentLastPeriods={getCurrentLastPeriods}
          getQueryRunningStatus={(queryRunning) => {
            setLastPeriodCostLoading(queryRunning);
          }}
        />
      )}
    </Stack>
  );
};

export default Step2;
